import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AxiosError } from 'axios';
import { isFuture, isPast } from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys.ts';

import { getPinsStatus, getPinsStatusFailure } from 'store/user/actions';
import { PIN_TYPES, PinStatus } from 'store/user/reducer.types';
import { selectAccessPinLockedTime } from 'store/user/selectors';

import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import isFunction from 'utils/function';

import validatePin from 'screens/Settings/ParentalControl/utils/validatePin';

import { PinDialogRef } from 'components/PinDialog';
import { PinDialogStatus } from 'components/PinDialog/hooks/getPinDialogText';

export default function usePinDialogStatus(
    pinDialogRef: RefObject<PinDialogRef | null>,
    visible?: boolean,
    onClose?: () => void,
) {
    const dispatch = useDispatch();

    const accessPinLockedTime = useSelector(selectAccessPinLockedTime);

    const prevAccessPinLockedTime = useRef(accessPinLockedTime);

    const [pinDialogStatus, setPinDialogStatus] = useState<PinDialogStatus>(
        accessPinLockedTime && !isPast(accessPinLockedTime)
            ? PinDialogStatus.BLOCKED
            : PinDialogStatus.ENTER_PIN,
    );

    const handleResetPinStatus = useCallback(() => {
        setPinDialogStatus(
            accessPinLockedTime && !isPast(accessPinLockedTime)
                ? PinDialogStatus.BLOCKED
                : PinDialogStatus.ENTER_PIN,
        );
    }, [accessPinLockedTime]);

    const handlePinNotValid = useCallback(() => {
        const onPinsStatusSuccess = (pinsStatuses: PinStatus[] | undefined) => {
            if (pinsStatuses) {
                const accessPinBlocked = pinsStatuses.some(
                    ({ pinType, lockTimeRemaining }) =>
                        pinType === PIN_TYPES.AccessPin &&
                        lockTimeRemaining &&
                        isFuture(lockTimeRemaining),
                );

                if (accessPinBlocked) {
                    setPinDialogStatus(PinDialogStatus.BLOCKED);
                } else {
                    setPinDialogStatus(PinDialogStatus.INCORRECT);
                }
            }

            pinDialogRef.current?.clear();
        };
        const retryGetPinsStatusAction = () =>
            new Promise<void>((resolve, reject) => {
                dispatch(
                    getPinsStatus({
                        onSuccess: (pinsStatuses) => {
                            onPinsStatusSuccess(pinsStatuses);
                            resolve();
                        },
                        onFailure: (error) => {
                            if (isErrorNeedsRetry(error)) {
                                reject(error);
                            } else {
                                setPinDialogStatus(PinDialogStatus.INCORRECT);
                                pinDialogRef.current?.clear();
                                resolve();
                            }
                        },
                    }),
                );
            });

        dispatch(
            getPinsStatus({
                onSuccess: onPinsStatusSuccess,
                onFailure: (error) => {
                    if (isErrorNeedsRetry(error)) {
                        RetryController.showVitalDialog({
                            actionButtonTitle: LANGUAGE_KEYS.CLOSE_BUTTON,
                            actionButtonOnPress: () => {
                                RetryController.hideVitalDialog();
                                setPinDialogStatus(PinDialogStatus.INCORRECT);
                                pinDialogRef.current?.clear();
                            },
                        });
                        RetryController.registerRetryCallback(
                            retryGetPinsStatusAction,
                            {
                                retryDuration: 10 * 60 * 1000,
                                maxRetryCount: 10,
                            },
                        );
                        RetryController.executeRetry();
                    } else {
                        setPinDialogStatus(PinDialogStatus.INCORRECT);
                        pinDialogRef.current?.clear();
                    }
                },
            }),
        );
    }, [dispatch, pinDialogRef]);

    const handleValidatePin = useCallback(
        async ({
            pin,
            onPinValid,
        }: {
            pin: string;
            onPinValid?: () => void;
        }) => {
            try {
                const response = await validatePin(pin);

                if (response.status === 200) {
                    pinDialogRef.current?.clear();

                    if (isFunction(onPinValid)) {
                        onPinValid();
                    }
                }
            } catch (error: any) {
                if (isErrorNeedsRetry(error)) {
                    dispatch(getPinsStatusFailure(error as AxiosError));

                    const handleCloseVitalDialog = () => {
                        onClose?.();
                        handleResetPinStatus();
                        RetryController.hideVitalDialog();
                    };

                    RetryController.showVitalDialog({
                        actionButtonTitle: LANGUAGE_KEYS.CLOSE_BUTTON,
                        actionButtonOnPress: handleCloseVitalDialog,
                        vitalDialogOnBackPress: handleCloseVitalDialog,
                    });
                } else {
                    handlePinNotValid();
                }
            }
        },
        [
            dispatch,
            handlePinNotValid,
            pinDialogRef,
            onClose,
            handleResetPinStatus,
        ],
    );

    useEffect(() => {
        if (prevAccessPinLockedTime.current && !accessPinLockedTime) {
            setPinDialogStatus(PinDialogStatus.ENTER_PIN);
        } else if (
            prevAccessPinLockedTime.current &&
            accessPinLockedTime &&
            !isPast(accessPinLockedTime)
        ) {
            setPinDialogStatus(PinDialogStatus.BLOCKED);
        }

        prevAccessPinLockedTime.current = accessPinLockedTime;
    }, [accessPinLockedTime, visible]);

    return {
        pinDialogStatus,
        setPinDialogStatus,
        onResetPinStatus: handleResetPinStatus,
        onValidatePin: handleValidatePin,
    };
}
